// v10/src/components/admin/aircraft/VisionImportModal.js
//
// R6 — Modal de import desde Claude Vision. Dos pasos:
//   1. Drag & drop o file picker → POST /import-from-vision → recibe items parseados (sin guardar)
//   2. Tabla preview con checkboxes (admin desmarca lo que no quiere) → onComplete(items, replaceExisting)

import React, { useState, useRef, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Upload, FileText, Image as ImageIcon, X, Loader, AlertCircle, Check, FileUp,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import aircraftEquipmentService from '../../../services/aircraftEquipmentService';

const MAX_SIZE = 10 * 1024 * 1024;  // 10 MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];

const CONFIDENCE_STYLES = {
  high:   { bg: 'rgba(34,197,94,0.12)',  color: '#16a34a', label: '🟢' },
  medium: { bg: 'rgba(245,158,11,0.12)', color: '#d97706', label: '🟡' },
  low:    { bg: 'rgba(249,115,22,0.16)', color: '#ea580c', label: '🟠' },
};

const formatSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const VisionImportModal = ({ aircraftId, aircraftLabel, onComplete, onClose, darkMode }) => {
  const { t } = useTranslation('admin');
  const fileInputRef = useRef(null);

  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const [processing, setProcessing] = useState(false);
  const [parseError, setParseError] = useState(null);

  const [parsedItems, setParsedItems] = useState(null);  // array de items o null
  const [skippedCount, setSkippedCount] = useState(0);
  const [modelUsed, setModelUsed] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const [replaceExisting, setReplaceExisting] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // ─── Step 1: file handling ─────────────────────────────────────
  const validateFile = (f) => {
    if (!ALLOWED_TYPES.includes(f.type)) {
      return t('vision_import_invalid_type');
    }
    if (f.size > MAX_SIZE) {
      return t('vision_import_file_too_large');
    }
    return null;
  };

  const onPickFile = (f) => {
    if (!f) return;
    const err = validateFile(f);
    if (err) {
      setFileError(err);
      setFile(null);
      return;
    }
    setFileError(null);
    setFile(f);
  };

  const onInputChange = (e) => {
    const f = e.target.files?.[0];
    if (f) onPickFile(f);
    e.target.value = '';
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) onPickFile(f);
  };

  // ─── Step 2: process ───────────────────────────────────────────
  const handleProcess = useCallback(async () => {
    if (!file) return;
    setProcessing(true);
    setParseError(null);
    try {
      const res = await aircraftEquipmentService.importFromVision(aircraftId, file);
      const data = res?.data?.data;
      const items = (data?.items || []).map((it, idx) => ({ ...it, _localId: `it-${idx}` }));
      setParsedItems(items);
      setSkippedCount(data?.skipped_count || 0);
      setModelUsed(data?.model_used || null);
      setSelectedIds(new Set(items.map(it => it._localId)));
    } catch (err) {
      const msg = err.response?.data?.message
        || err.response?.data?.detail
        || t('vision_import_process_error');
      setParseError(msg);
    } finally {
      setProcessing(false);
    }
  }, [aircraftId, file, t]);

  // ─── Step 3: edit items in-place before confirming ────────────
  const updateItemField = (localId, field, value) => {
    setParsedItems(items => items.map(it =>
      it._localId === localId ? { ...it, [field]: value } : it
    ));
  };

  const toggleSelected = (localId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(localId)) next.delete(localId);
      else next.add(localId);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === parsedItems.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(parsedItems.map(it => it._localId)));
    }
  };

  const lowConfidenceCount = useMemo(
    () => (parsedItems || []).filter(it => it.confidence === 'low').length,
    [parsedItems]
  );

  const selectedCount = selectedIds.size;
  const totalCount = parsedItems?.length || 0;

  const handleConfirm = async () => {
    if (!parsedItems || selectedCount === 0) return;
    setSubmitting(true);
    try {
      const items = parsedItems
        .filter(it => selectedIds.has(it._localId))
        .map(it => ({
          item_number: it.item_number || null,
          description: it.description,
          weight_lb: Number(it.weight_lb),
          arm_in: Number(it.arm_in),
          is_installed: it.is_installed !== false,
          is_required: !!it.is_required,
          source: 'vision',
          confidence: it.confidence || null,
        }))
        .filter(it => it.description && !Number.isNaN(it.weight_lb) && !Number.isNaN(it.arm_in));

      await onComplete(items, replaceExisting);
      // El padre cierra el modal en éxito.
    } catch {
      setSubmitting(false);
    }
  };

  // ─── styles ───────────────────────────────────────────────────
  const cardBg = darkMode ? '#111827' : '#fff';
  const borderColor = darkMode ? '#1f2937' : '#e2e8f0';
  const textPrimary = darkMode ? '#fff' : '#0f172a';
  const textMuted = darkMode ? 'rgba(255,255,255,0.55)' : '#64748b';

  const thStyle = {
    padding: '8px 10px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
    color: textMuted, borderBottom: `1px solid ${borderColor}`, position: 'sticky', top: 0,
    background: darkMode ? '#0b1220' : '#f8fafc',
  };
  const tdStyle = { padding: '8px 10px', fontSize: 13, borderBottom: `1px solid ${darkMode ? '#1f2937' : '#f1f5f9'}`, color: textPrimary };

  const cellInput = {
    width: '100%', padding: '4px 6px', borderRadius: 6, fontSize: 12,
    background: 'transparent', color: textPrimary,
    border: `1px solid transparent`,
  };
  const cellInputFocus = `1px solid ${darkMode ? '#374151' : '#cbd5e1'}`;

  // ─── render ───────────────────────────────────────────────────
  const renderStep1 = () => (
    <div>
      <h4 style={{ margin: '0 0 12px', fontWeight: 700, fontSize: 14, color: textPrimary }}>
        {t('vision_import_step1_title')}
      </h4>

      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => fileInputRef.current?.click()}
        style={{
          border: `2px dashed ${dragOver ? '#2563eb' : borderColor}`,
          borderRadius: 14,
          padding: '32px 16px',
          textAlign: 'center',
          cursor: 'pointer',
          background: dragOver
            ? (darkMode ? 'rgba(37,99,235,0.08)' : 'rgba(37,99,235,0.04)')
            : (darkMode ? 'rgba(255,255,255,0.02)' : '#f8fafc'),
          transition: 'all 0.2s',
        }}
      >
        <Upload size={28} style={{ color: dragOver ? '#2563eb' : textMuted, marginBottom: 8 }} />
        <p style={{ margin: 0, fontWeight: 700, fontSize: 14, color: textPrimary }}>
          {t('vision_import_dropzone')}
        </p>
        <p style={{ margin: '4px 0 0', fontSize: 12, color: textMuted }}>
          {t('vision_import_dropzone_help')}
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,application/pdf"
          onChange={onInputChange}
          style={{ display: 'none' }}
        />
      </div>

      {file && (
        <div style={{
          marginTop: 12, padding: '10px 12px', borderRadius: 12,
          background: darkMode ? 'rgba(255,255,255,0.04)' : '#f1f5f9',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          {file.type === 'application/pdf'
            ? <FileText size={16} style={{ color: '#2563eb' }} />
            : <ImageIcon size={16} style={{ color: '#2563eb' }} />}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {file.name}
            </div>
            <div style={{ fontSize: 11, color: textMuted }}>{formatSize(file.size)}</div>
          </div>
          <button onClick={(e) => { e.stopPropagation(); setFile(null); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: textMuted, padding: 4 }}>
            <X size={14} />
          </button>
        </div>
      )}

      {fileError && (
        <div style={{ marginTop: 10, fontSize: 12, color: '#dc2626', display: 'flex', alignItems: 'center', gap: 6 }}>
          <AlertCircle size={14} /> {fileError}
        </div>
      )}

      {parseError && (
        <div style={{ marginTop: 10, padding: 10, borderRadius: 10, background: 'rgba(239,68,68,0.08)', color: '#b91c1c', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
          <AlertCircle size={14} /> {parseError}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
        <button onClick={onClose} disabled={processing}
          style={{ padding: '8px 14px', borderRadius: 10, border: 'none', cursor: 'pointer',
            background: darkMode ? '#374151' : '#f1f5f9',
            color: darkMode ? '#fff' : '#0f172a', fontWeight: 600, fontSize: 13 }}>
          {t('vision_import_cancel')}
        </button>
        <button onClick={handleProcess} disabled={!file || processing}
          style={{ padding: '8px 14px', borderRadius: 10, border: 'none', cursor: 'pointer',
            background: '#2563eb', color: '#fff', fontWeight: 700, fontSize: 13,
            opacity: (!file || processing) ? 0.55 : 1,
            display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {processing
            ? (<><Loader size={14} className="animate-spin" /> {t('vision_import_processing')}</>)
            : (<><FileUp size={14} /> {t('vision_import_process_button')}</>)}
        </button>
      </div>

      {processing && (
        <p style={{ marginTop: 8, fontSize: 11, color: textMuted, textAlign: 'right' }}>
          {t('vision_import_processing_help')}
        </p>
      )}
    </div>
  );

  const renderStep2 = () => (
    <div>
      <h4 style={{ margin: '0 0 8px', fontWeight: 700, fontSize: 14, color: textPrimary }}>
        {t('vision_import_step2_title')}
      </h4>
      <p style={{ margin: '0 0 12px', fontSize: 12, color: textMuted }}>
        <Check size={12} style={{ display: 'inline', verticalAlign: 'middle', color: '#16a34a' }} />{' '}
        {t('vision_import_items_detected', { count: totalCount })}
        {lowConfidenceCount > 0 && (
          <> · {t('vision_import_items_low_confidence', { count: lowConfidenceCount })}</>
        )}
        {skippedCount > 0 && (
          <> · {t('vision_import_items_skipped', { count: skippedCount })}</>
        )}
        {modelUsed && (
          <span style={{ marginLeft: 8, fontSize: 10, padding: '1px 6px', borderRadius: 6,
            background: darkMode ? 'rgba(255,255,255,0.06)' : '#e2e8f0' }}>
            {modelUsed}
          </span>
        )}
      </p>

      {totalCount === 0 ? (
        <div style={{ padding: 16, borderRadius: 12, background: 'rgba(245,158,11,0.1)', color: '#92400e', fontSize: 13 }}>
          <AlertCircle size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} />
          {t('vision_import_no_items')}
        </div>
      ) : (
        <div style={{ maxHeight: '46vh', overflowY: 'auto', borderRadius: 12, border: `1px solid ${borderColor}` }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, width: 36, textAlign: 'center' }}>
                  <input type="checkbox"
                    checked={selectedCount === totalCount && totalCount > 0}
                    onChange={toggleAll}
                  />
                </th>
                <th style={{ ...thStyle, width: 70 }}>{t('equipment_col_number')}</th>
                <th style={thStyle}>{t('equipment_col_description')}</th>
                <th style={{ ...thStyle, width: 100, textAlign: 'right' }}>{t('equipment_col_weight')}</th>
                <th style={{ ...thStyle, width: 100, textAlign: 'right' }}>{t('equipment_col_arm')}</th>
                <th style={{ ...thStyle, width: 60 }}>{t('equipment_col_confidence')}</th>
              </tr>
            </thead>
            <tbody>
              {parsedItems.map(it => {
                const conf = CONFIDENCE_STYLES[it.confidence] || CONFIDENCE_STYLES.medium;
                const selected = selectedIds.has(it._localId);
                return (
                  <tr key={it._localId} style={{ opacity: selected ? 1 : 0.45 }}>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      <input type="checkbox" checked={selected} onChange={() => toggleSelected(it._localId)} />
                    </td>
                    <td style={{ ...tdStyle, fontFamily: 'monospace' }}>
                      <input value={it.item_number || ''}
                        onChange={e => updateItemField(it._localId, 'item_number', e.target.value)}
                        onFocus={e => { e.target.style.border = cellInputFocus; }}
                        onBlur={e => { e.target.style.border = '1px solid transparent'; }}
                        style={cellInput} />
                    </td>
                    <td style={tdStyle}>
                      <input value={it.description}
                        onChange={e => updateItemField(it._localId, 'description', e.target.value)}
                        onFocus={e => { e.target.style.border = cellInputFocus; }}
                        onBlur={e => { e.target.style.border = '1px solid transparent'; }}
                        style={cellInput} />
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      <input type="number" step="0.01" value={it.weight_lb}
                        onChange={e => updateItemField(it._localId, 'weight_lb', e.target.value)}
                        onFocus={e => { e.target.style.border = cellInputFocus; }}
                        onBlur={e => { e.target.style.border = '1px solid transparent'; }}
                        style={{ ...cellInput, textAlign: 'right' }} />
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      <input type="number" step="0.01" value={it.arm_in}
                        onChange={e => updateItemField(it._localId, 'arm_in', e.target.value)}
                        onFocus={e => { e.target.style.border = cellInputFocus; }}
                        onBlur={e => { e.target.style.border = '1px solid transparent'; }}
                        style={{ ...cellInput, textAlign: 'right' }} />
                    </td>
                    <td style={tdStyle}>
                      <span title={t(`vision_import_legend_${it.confidence || 'medium'}`)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
                          padding: '1px 6px', borderRadius: 6, fontSize: 10, fontWeight: 700,
                          background: conf.bg, color: conf.color }}>
                        {conf.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {totalCount > 0 && (
        <div style={{ marginTop: 10, fontSize: 11, color: textMuted }}>
          🟢 {t('vision_import_legend_high')} · 🟡 {t('vision_import_legend_medium')} · 🟠 {t('vision_import_legend_low')}
        </div>
      )}

      {totalCount > 0 && (
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 14, fontSize: 13, color: textPrimary }}>
          <input type="checkbox" checked={replaceExisting} onChange={e => setReplaceExisting(e.target.checked)}
            style={{ marginTop: 3 }} />
          <span>
            {t('vision_import_replace_label')}
            {replaceExisting && (
              <div style={{ marginTop: 4, fontSize: 11, color: '#b91c1c', fontWeight: 600 }}>
                {t('vision_import_replace_warning')}
              </div>
            )}
          </span>
        </label>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
        <button onClick={onClose} disabled={submitting}
          style={{ padding: '8px 14px', borderRadius: 10, border: 'none', cursor: 'pointer',
            background: darkMode ? '#374151' : '#f1f5f9',
            color: darkMode ? '#fff' : '#0f172a', fontWeight: 600, fontSize: 13 }}>
          {t('vision_import_cancel')}
        </button>
        <button onClick={handleConfirm} disabled={submitting || selectedCount === 0}
          style={{ padding: '8px 14px', borderRadius: 10, border: 'none', cursor: 'pointer',
            background: '#16a34a', color: '#fff', fontWeight: 700, fontSize: 13,
            opacity: (submitting || selectedCount === 0) ? 0.55 : 1,
            display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {submitting ? <Loader size={14} className="animate-spin" /> : <Check size={14} />}
          {t('vision_import_confirm', { count: selectedCount })}
        </button>
      </div>
    </div>
  );

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}>
      <motion.div initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 10 }}
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 760, maxHeight: '90vh',
          background: cardBg, borderRadius: 18,
          border: `1px solid ${borderColor}`,
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '16px 20px',
          borderBottom: `1px solid ${borderColor}`,
        }}>
          <FileUp size={20} style={{ color: '#2563eb' }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3 style={{ margin: 0, fontWeight: 700, fontSize: 16, color: textPrimary }}>
              {t('vision_import_title')}
            </h3>
            {aircraftLabel && (
              <p style={{ margin: 0, fontSize: 12, color: textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {aircraftLabel}
              </p>
            )}
          </div>
          <button onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: textMuted, padding: 4 }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1 }}>
          {parsedItems === null ? renderStep1() : renderStep2()}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default VisionImportModal;
